import React, { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useCustomPages } from "@/components/pages/custom-pages/hooks/useCustomPages";
import { CustomPage } from "@/types/custom-page";

interface AddPageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (page: CustomPage) => void;
  existingIds: string[];
}

export const AddPageDialog: React.FC<AddPageDialogProps> = ({
  open,
  onOpenChange,
  onAdd,
  existingIds,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const { data: customPages = [], isLoading } = useCustomPages();

  useEffect(() => {
    if (!open) {
      setSearch("");
    }
  }, [open]);

  const filteredPages = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return customPages.filter((page) => {
      if (existingIds.includes(page.id)) {
        return false;
      }

      if (normalizedSearch.length === 0) {
        return true;
      }

      return (
        page.title.toLowerCase().includes(normalizedSearch) ||
        page.buttonTitle.toLowerCase().includes(normalizedSearch)
      );
    });
  }, [customPages, existingIds, search]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t("mobileSettings.dashboard.addPage", "Add Page")}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t(
                "mobileSettings.dashboard.searchPages",
                "Search pages...",
              )}
              className="pl-9"
            />
          </div>

          <div className="custom-scrollbar h-[400px] overflow-y-auto pr-4">
            {isLoading ? (
              <div className="py-8 text-center text-zinc-500">
                {t("mobileSettings.dashboard.loadingPages", "Loading pages...")}
              </div>
            ) : null}

            {!isLoading && filteredPages.length === 0 ? (
              <div className="py-8 text-center text-zinc-500">
                {t("mobileSettings.dashboard.noPagesFound", "No pages found")}
              </div>
            ) : null}

            {!isLoading && filteredPages.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {filteredPages.map((page) => (
                  <div
                    key={page.id}
                    className="flex cursor-pointer flex-col gap-3 rounded-xl border bg-zinc-50/50 p-3 transition-colors hover:border-zinc-900"
                    onClick={() => {
                      onAdd(page);
                      onOpenChange(false);
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <Badge
                        variant="secondary"
                        className="bg-zinc-200 px-2 py-0 text-[10px] text-zinc-900"
                      >
                        {page.enabled
                          ? t("mobileSettings.dashboard.pageEnabled", "Enabled")
                          : t(
                              "mobileSettings.dashboard.pageDisabled",
                              "Disabled",
                            )}
                      </Badge>
                    </div>

                    <div className="flex flex-col gap-1">
                      <span className="line-clamp-2 text-sm font-medium">
                        {page.title}
                      </span>
                      <span className="line-clamp-2 text-xs text-zinc-500">
                        {page.buttonTitle}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
